import {useEffect, useState} from "react";

import {withTranslation} from "react-i18next";
import {
  Backdrop,
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  TextField,
  Typography
} from "@mui/material";
import {Leaderboard, TrendingUp} from "@mui/icons-material";
import BaseConfigModal, { CONFIG_TAB_SX } from "@plannel/components/modal/BaseConfigModal";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import planConfigService from "@plannel/services/data/plan-config-service";
import CodeUtil from "@plannel/pages/utils/code-util";
import {includes, isArray, isEqual, keyBy, size, values} from "lodash";
import measureService from "@plannel/services/data/measure-service";
import Tabs from "@mui/material/Tabs";
import { FS } from "@plannel/utils/font-util";

const WORKBENCH_THRESHOLD = "WORKBENCH_THRESHOLD";
const DP_PLAN_THRESHOLD = "DP_PLAN_THRESHOLD";
const DP_PLAN_CUM_THRESHOLD = "DP_PLAN_CUM_THRESHOLD";
const DP_TRACKING_THRESHOLD = "DP_TRACKING_THRESHOLD";

const thresholdConfigCds = [
  WORKBENCH_THRESHOLD, DP_PLAN_THRESHOLD, DP_PLAN_CUM_THRESHOLD, DP_TRACKING_THRESHOLD
];

const MeasureCard = (props) => {
  const { t, idx, page, thresholdConfigCds, changeThresholdValue, thresholdMap, getReferMeasures, getTargetMeasures, isError
  } = props
  const configCd = thresholdConfigCds?.[idx];
  const thresholdObj = thresholdMap?.[configCd] || {};

  const { workbenchAlertObjs: { REFER_MEASURE, TARGET_MEASURE, LOWER_THRESHOLD, UPPER_THRESHOLD } } = CodeUtil;

  const refMeasure = thresholdObj?.[REFER_MEASURE] || "";
  const targetMeasure = thresholdObj?.[TARGET_MEASURE] || "";
  const upperThreshold = thresholdObj?.[UPPER_THRESHOLD] || 0;
  const lowerThreshold = thresholdObj?.[LOWER_THRESHOLD] || 0;

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        width: 250,
      },
    },
  };

  const handleChange = (configCd, target, value) => {
    changeThresholdValue(configCd, target, value);
  }

  return (
    /*
     * Card/CardContent 를 걷어냈다 — 셸(BaseConfigModal)이 이미 테두리를 그려서 outlined Card 는
     * 테두리가 겹치고, 테두리만 지우면 CardContent 의 16px 패딩이 이유 없는 들여쓰기로 남는다.
     * 두 섹션 간격은 아래 mt 로 직접 준다.
     */
    <Box>
      <Box>
        <Box sx={{ display: "flex", width: "100%" }}>
          <Leaderboard
            fontSize={"medium"}
            sx={{ mb: 0.2, mr: 1 }}
          />
          <Typography
            id="modal-modal-title"
            variant="subtitle1"
            sx={{ width: "100%" }}>
            {t("comparMeasureSetting")}
          </Typography>
        </Box>
        {/* size="small" 은 Select/TextField 에 직접 줘야 한다 — 감싸는 Box 나 FormControl 로는 안 내려간다 */}
        <Box sx={{ mt: 1, gap: 1, display: "flex", textAlign: "center" }}>
          <FormControl size="small" sx={{ width: "100%" }}>
            <InputLabel>{t("refMeasure")}</InputLabel>
            <Select
              size="small"
              value={refMeasure}
              label={t("refMeasure")}
              MenuProps={MenuProps}
              sx={{ fontSize: FS.TITLE }}
              onChange={event => handleChange(configCd, REFER_MEASURE, event.target.value)}>
              {getReferMeasures(page)?.map(measure => (
                <MenuItem value={measure.cd} key={measure.cd} sx={{ fontSize: FS.TITLE }}>{t(measure.name)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: "100%" }} error={isError}>
            <InputLabel>{t("targetMeasure")}</InputLabel>
            <Select
              size="small"
              value={targetMeasure}
              label={t("targetMeasure")}
              MenuProps={MenuProps}
              sx={{ fontSize: FS.TITLE }}
              onChange={event => handleChange(configCd, TARGET_MEASURE, event.target.value)}>
              {getTargetMeasures(configCd, refMeasure)?.map(measure => (
                <MenuItem value={measure.cd} key={measure.cd} sx={{ fontSize: FS.TITLE }}>{t(measure.name)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex" }}>
          <TrendingUp
            fontSize={"medium"}
            sx={{ mb: 0.2, mr: 1 }}
          />
          <Typography
            id="modal-modal-title"
            variant="subtitle1">
            {t("measureThreshold")}
          </Typography>
        </Box>
        {/* TextField 는 자기 FormControl 을 따로 만든다 — 바깥 FormControl 의 size 가 안 내려가므로 직접 준다 */}
        <Box sx={{ mt: 1, gap: 1, display: "flex", textAlign: "center" }}>
          <TextField
            size="small"
            sx={{ width: "100%", "& .MuiInputBase-input": { fontSize: FS.TITLE } }}
            label={t("measureLowerThres")}
            value={lowerThreshold}
            type="number"
            InputProps={{ inputProps: { min: -100, max: 0 }, endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            onChange={(event) => handleChange(configCd, LOWER_THRESHOLD, event.target.value)}
          />
          <TextField
            size="small"
            sx={{ width: "100%", "& .MuiInputBase-input": { fontSize: FS.TITLE } }}
            label={t("measureUpperThres")}
            value={upperThreshold}
            type="number"
            InputProps={{ inputProps: { min: 0, max: 100 }, endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            onChange={(event) => handleChange(configCd, UPPER_THRESHOLD, event.target.value)}
          />
        </Box>
      </Box>
    </Box>
  );
}


const DpMeasureAlertSettings = props => {
  const { t, close, open } = props;

  const { workbenchAlertObjs: { REFER_MEASURE, TARGET_MEASURE, LOWER_THRESHOLD, UPPER_THRESHOLD },
      module: { DP }, dpMeasureType: { AMT, QTY }, toggleType, thresholdTargetMeasures, getDpPlanActualAlertMeasures, dpTrackingMeasures } = CodeUtil;
  const { SA_QTY, SA_AMT } = CodeUtil.dpMeasure;
  const {  GROSS_PROFIT, PLAN_PROFIT, FIN_PROFIT } = getDpPlanActualAlertMeasures();
  const profitMeasure = [GROSS_PROFIT.name, PLAN_PROFIT.name, FIN_PROFIT.name];

  const { snackBarMessageColor: { saveSuccess, saveFail, inputError  } } = ColorUtil;
  const menuList = [ t("menuDpPractitionerWorkbench"), t("menuDpPlanVsActual"), t("menuDpPlanVsActualCum"), t("menuDpTrackingReport")];
  const [finishCreate, setFinishCreate] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [oriMeasureMap, setOriMeasureMap] = useState([]);

  const [thresholdMap, setThresholdMap] = useState({});
  const [oriThresholdList, setOriThresholdList] = useState([]);
  const [isError, setIsError] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const handleClose = () => {
    close(false);
  };

  const retrievePlanConfig = () => {
    const paramList = thresholdConfigCds.map(cd => {
      return {
        moduleCd: DP,
        activeFlg: true,
        configCd: cd
      }
    });

    planConfigService.getConfigCds(paramList)
      .then((response) => {
        if (response?.data) {
          setOriThresholdList(response.data);
          const configMapObj = response?.data.reduce((acc, cur) => {
            acc[cur.configCd] = cur.objVal || {};
            return acc;
          }, {});

          setThresholdMap(configMapObj);
        }
      }).catch(e => {
        debug(e);
      })
  };

  const handleSave = () => {
    setFinishCreate(true);
    const params = [];

    const planConfigCd = thresholdConfigCds[tabIndex];
    const planConfig = oriThresholdList?.filter(item => item.configCd === planConfigCd)?.[0] || {};

    if (size(planConfig) > 0) {
      planConfig.objVal = thresholdMap?.[planConfigCd] || {};
      params.push({...planConfig});
    } else {
      params.push({
        moduleCd: DP,
        configCd: planConfigCd,
        activeFlg: true,
        objVal: thresholdMap?.[planConfigCd] || {}
      })
    }
    const lowerThreshold = thresholdMap?.[planConfigCd]?.[LOWER_THRESHOLD] || 0;
    const upperThreshold = thresholdMap?.[planConfigCd]?.[UPPER_THRESHOLD] || 0;

    const lowerError = lowerThreshold > 0 || lowerThreshold < -100;
    const upperError = upperThreshold > 100 || upperThreshold < 0;

    if (lowerError) {
      setSnackInfo({ open: true, severity: inputError, content: `${t("MSG_ValueBetween")} -100 ~ 0` });
      setFinishCreate(false);
    } else if (upperError) {
      setSnackInfo({ open: true, severity: inputError, content: `${t("MSG_ValueBetween")} 0 ~ 100` });
      setFinishCreate(false);
    } else if (isError) {
      setSnackInfo({ open: true, severity: inputError, content: t("MSG_SelectDiffrMeasure") });
      setFinishCreate(false);
    }

    if (!isError && !lowerError && !upperError) {
      planConfigService.upsert(params)
        .then(() => {
          setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
          close(true);
          setFinishCreate(false);
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          setFinishCreate(false);
        })
    }
  };

  const handleDialog = action => {
    if (action === "SAVE") {
      handleSave();
    }
  };

  const sortListByColumn = (list, columnName) => {
    list.sort((a, b) => {
      if (a[columnName] > b[columnName]) {
        return 1;
      }
      if (a[columnName] < b[columnName]) {
        return -1;
      }
      return 0;
    });

    return list;
  }

  const getMeasureList = (measureMap) => {
    const types = [QTY, AMT, toggleType.QTY, toggleType.AMT];
    const list = [];
    if (isArray(measureMap)) {
      for (const measure of measureMap) {
        if (includes(types, measure.valueTypeCd)) {
          const obj = {
            name: measure.name,
            cd: measure.measureCd,
            simFlg: measure.measureCd.includes("USER_MEASURE") ? true : false
          }
          list.push(obj);
        }
      }
    } else {
      for (const key in measureMap) {
        if (Object.prototype.hasOwnProperty.call(measureMap, key)) {
          const measure = measureMap[key];
          if (includes(types, measure?.type)) {
            const obj = {
              name: measure.name,
              cd: includes(profitMeasure, measure.name) ? measure.name : measure?.cd ?? key,
              simFlg: true
            };
            list.push(obj);
          }
        }
      }
    }
    return list;
  }

  const getReferMeasures = page => {
    const list = [];
    const dpPlanActualMeasures = getDpPlanActualAlertMeasures();

    if (page === t("menuDpPractitionerWorkbench")) {
      const measureList = values(oriMeasureMap);
      if (measureList && isArray(measureList)) {
        list.push(...getMeasureList(measureList));
      }
    } else if (page === t("menuDpPlanVsActual") || page === t("menuDpPlanVsActualCum")) {
      list.push(...getMeasureList(dpPlanActualMeasures));
    } else if (page === t("menuDpTrackingReport")) {
      for (const measure of dpTrackingMeasures) {
        if (includes([SA_AMT.cd, SA_QTY.cd], measure.cd)) {
          const obj = {
            name: measure.name,
            cd: measure.cd,
            simFlg: false
          };
          list.push(obj);
        }
      }
    }

    //sort
    let sortList = sortListByColumn(list, "name");
    sortList = sortListByColumn(list, "simFlg");

    const nullContent = { name: "none", cd: "" };
    return [nullContent, ...sortList];
  }

  const getTargetMeasures = (configCd, refMeasure) => {
    const list = [];
    let measureList = [];
    const dpPlanActualMeasures = getDpPlanActualAlertMeasures();
    const { BUDGET_PLAN_Q, BUDGET_PLAN_A, DEMAND_PLAN_Q, DEMAND_PLAN_A } = dpPlanActualMeasures;

    switch (configCd) {
      case WORKBENCH_THRESHOLD:
        measureList = thresholdTargetMeasures;
        break;
      case DP_PLAN_THRESHOLD:
        measureList = getMeasureList(dpPlanActualMeasures);
        break;
      case DP_PLAN_CUM_THRESHOLD:
        measureList = getMeasureList(dpPlanActualMeasures);
        break;
      case DP_TRACKING_THRESHOLD:
        measureList = dpTrackingMeasures.map(measure => ({
          name: measure.name,
          cd: measure.cd,
          simFlg: false
        }));
        break;
      default:
        measureList = [];
    }


    if (isEqual(refMeasure, "BUDGET_PLAN_Q")){
      refMeasure = BUDGET_PLAN_Q.cd;
    } else if (isEqual(refMeasure, "BUDGET_PLAN_A")){
      refMeasure = BUDGET_PLAN_A.cd;
    } else if (isEqual(refMeasure, "DEMAND_PLAN_Q")){
      refMeasure = DEMAND_PLAN_Q.cd;
    } else if (isEqual(refMeasure, "DEMAND_PLAN_A")){
      refMeasure = DEMAND_PLAN_A.cd;
    }

    const referObj = oriMeasureMap?.[refMeasure];

    const type = referObj?.valueTypeCd;
    const customTypes = type === AMT ? "_A" : "_Q";
    const isProfitMeasure = includes(profitMeasure, refMeasure);

    for (const measure of measureList) {
      if (measure.cd === refMeasure) {
        continue;
      }
      // dp tracking exclude sa amt/qty
      if (configCd === DP_TRACKING_THRESHOLD && includes([SA_AMT.cd, SA_QTY.cd], measure.cd)) {
        continue;
      }

      if (isProfitMeasure && includes(profitMeasure, measure.name)) {
        list.push({
          name: measure.name,
          cd: measure.name
        });
      } else if (!isProfitMeasure) {
        if (includes(measure.cd, type) || includes(measure.cd, customTypes)) {
          list.push({
            name: measure.name,
            cd: measure.cd
          });
        }
      }
    }

   //sort
    const sortList = sortListByColumn(list, "cd");

    const nullContent = { name: "none", cd: "" };
    return [nullContent, ...sortList];
  }

  const retrieveMeasures = () => {
    measureService.getAll({ isBasic: true })
    .then(response => {
      if (response.data?.results) {
        const result = response.data.results;
        const filteredReseult = result.filter(re => !re.simFlg);
        setOriMeasureMap(keyBy(filteredReseult, "measureCd"));
      }
    })
    .catch(e => {
      debug(e);
    })
    .finally(() => {
      retrievePlanConfig();
    });
  }

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (open) {
      retrieveMeasures();
      retrievePlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const changeThresholdValue = (configCd, target, value) => {
    setThresholdMap(prev => {
      const newThresholdMap = { ...prev };
      const thresholdObj = newThresholdMap?.[configCd] || {};

      thresholdObj[target] = value;
      newThresholdMap[configCd] = thresholdObj;
      return newThresholdMap;
    });

    if (target === REFER_MEASURE) {
      if (value) {
        getTargetMeasures(configCd, value);
      }
      setThresholdMap(prev => {
        const newThresholdMap = { ...prev };
        const thresholdObj = newThresholdMap?.[configCd] || {};

        thresholdObj[TARGET_MEASURE] = "";
        newThresholdMap[configCd] = thresholdObj;
        return newThresholdMap;
      });
    } else if (target === TARGET_MEASURE) {
      const referMeasure = thresholdMap?.[configCd]?.[REFER_MEASURE] || "";
      setIsError(isEqual(value, referMeasure));
    }
  }

  return (
    <>
      <Backdrop
        sx={{ display: "flex", color: "#fff", zIndex: 10000 }}
        open={finishCreate}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("measureAlertSetting")}
        onSave={handleSave}
        // 임계값이 정수가 아니면 저장을 막는다(원래 Save 버튼에 걸려 있던 조건)
        saveDisabled={
          !Number.isInteger(Number(thresholdMap?.[thresholdConfigCds[tabIndex]]?.[LOWER_THRESHOLD]))
          || !Number.isInteger(Number(thresholdMap?.[thresholdConfigCds[tabIndex]]?.[UPPER_THRESHOLD]))
        }
        size="lg">
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={tabIndex}
            onChange={handleTabChange}
            sx={CONFIG_TAB_SX}
            variant="scrollable"
            scrollButtons="auto"
          >
            {menuList.map((menu) => (
              <Tab label={menu} key={menu} />
            ))}
          </Tabs>
        </Box>
        <MeasureCard
          t={t}
          thresholdConfigCds={thresholdConfigCds}
          page={menuList?.[tabIndex]}
          idx={tabIndex}
          thresholdMap={thresholdMap}
          changeThresholdValue={changeThresholdValue}
          getReferMeasures={getReferMeasures}
          getTargetMeasures={getTargetMeasures}
          isError={isError}
          setIsError={setIsError}
          setThresholdMap={setThresholdMap}
        />
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  );
};

export default withTranslation()(DpMeasureAlertSettings);
